import { ComponentProps, ReactNode, useEffect } from 'react';

import cx from 'classnames';
import qs from 'query-string';
import { ToggleLayer } from 'react-laag';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import Title from 'components/shared/Typography/Title';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { setOwnershipAction } from 'store/admin/files';
import { ownershipSelector } from 'store/admin/files/selectors';
import { loadAssetsListThunk } from 'store/admin/files/thunks';
import { getSearchFiltersThunk } from 'store/admin/pagesTable/thunks';

import Button from '../Button';
import ArrowUp from '../Icon/icons/ArrowUp';
import { Menu } from '../Menu';
import s from './index.module.css';

type PageHeaderProps = {
  title?: string;
  field?: ReactNode;
  button?: ReactNode;
  ownership?: OwnershipDropdownProps['ownership'];
  onClick?: () => void;
  isFieldFirstChild?: boolean;
  className?: string;
};

function PageHeader(props: PageHeaderProps) {
  const { title, field, button, ownership, onClick, className } = props;
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getSearchFiltersThunk('media_library'));
    dispatch(loadAssetsListThunk('media', true, true, true));
  }, []);

  return (
    <div className={cx(s.wrap, className)} onClick={onClick} data-e2e-widget-type="pageHeader">
      <div className={s.titleWithFilter}>
        {title && (
          <Title
            italic
            level={4}
            weight="normal"
            noTransform
            noMargin
            className={s.title}
            data-e2e-element-id="headerTitle"
          >
            {title}
          </Title>
        )}
        {field}
      </div>
      <div className={s.actions} data-e2e-widget-type="headerActions">
        {ownership && <OwnershipDropdown ownership={ownership} />}
        {button}
      </div>
    </div>
  );
}

PageHeader.Button = HeaderButton;

export default PageHeader;

function HeaderButton(props: ComponentProps<typeof Button>) {
  const { isTabletOrMobile } = useMediaBreakpoints();
  return (
    <Button
      size="medium"
      shape="ellipse"
      {...props}
      children={isTabletOrMobile && props.icon ? null : props.children}
      iconProps={{
        fill: 'none',
        width: '22px',
        height: '22px',
        ...props.iconProps,
      }}
      className={cx(s.button, { [s.icon]: props.icon }, props.className)}
    />
  );
}

type OwnershipDropdownProps = {
  ownership: {
    module: string;
    options: Record<string, { name: string }>;
  };
};

function OwnershipDropdown(props: OwnershipDropdownProps) {
  const { ownership } = props;
  const dispatch = useDispatch();
  const currentFilter = useSelector(ownershipSelector);
  const history = useHistory();
  const handleUseFilter = (own: string, close: () => void) => {
    dispatch(setOwnershipAction(own));
    dispatch(loadAssetsListThunk(ownership.module, true, true, true));
    close();
    history.push({ search: qs.stringify({ ownership: own }) });
  };

  return (
    <ToggleLayer
      renderLayer={({ isOpen, layerProps, close }) => (
        <Menu {...layerProps} open={isOpen}>
          {Object.keys(ownership.options).map((key) => (
            <Menu.Option
              key={key}
              className={cx(s.filterItem, currentFilter === key && s.active)}
              onClick={() => handleUseFilter(key, close)}
            >
              {ownership.options[key].name}
            </Menu.Option>
          ))}
        </Menu>
      )}
      placement={{
        anchor: 'BOTTOM_CENTER',
      }}
      closeOnOutsideClick
    >
      {({ triggerRef, toggle }) => (
        <PageHeader.Button
          type="default"
          onClick={toggle}
          anchorRef={triggerRef}
          data-e2e-element-id={`${ownership.module}Filter`}
          icon={(props) => <ArrowUp {...props} fill="currentColor" className={s.ownershipButtonIcon} />}
        >
          {ownership.options[currentFilter]?.name}
        </PageHeader.Button>
      )}
    </ToggleLayer>
  );
}
